import { useState } from "react";
import {
  PhoneCall,
  UserRound,
  Mail,
  Wrench,
  Droplets,
  Bug,
  Trees,
  Car,
  Phone,
  Quote,
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  PhoneOutgoing,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const DEMO_URL = "https://cal.com/modern-path-ai-pludn4";
const EMILY_PHONE_DISPLAY = "1-888-907-7767";
const EMILY_PHONE_TEL = "+18889077767";

const openDemo = () => {
  if (typeof window !== "undefined") {
    window.open(DEMO_URL, "_blank", "noopener,noreferrer");
  }
};

const callEmily = () => {
  if (typeof window !== "undefined") {
    window.location.href = `tel:${EMILY_PHONE_TEL}`;
  }
};

/* ---------- Reusable bits ---------- */

const Logo = () => (
  <div
    className="flex items-center gap-2.5"
    data-testid="site-logo"
    style={{ fontFamily: "Space Grotesk, sans-serif" }}
  >
    <span className="text-[15px] sm:text-base font-bold tracking-tight text-white uppercase">
      Modern Path <span className="mp-cyan">Agency</span>
    </span>
  </div>
);

const PrimaryCta = ({ children, testId, onClick = openDemo, className = "" }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`mp-cta group ${className}`}
  >
    <span>{children}</span>
    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
  </button>
);

const Eyebrow = ({ children, className = "" }) => (
  <p className={`mp-eyebrow ${className}`}>{children}</p>
);

/* ---------- Sections ---------- */

const Hero = () => (
  <section
    data-testid="hero-section"
    className="relative min-h-screen flex items-center justify-center overflow-hidden px-4 pt-8 pb-20 sm:pb-28 bg-[#0A0A0A]"
  >
    <div className="mp-hero-glow" aria-hidden="true" />

    {/* Top nav */}
    <div className="absolute top-0 left-0 right-0 z-20">
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-5 sm:px-8 py-6">
        <Logo />
        <button
          onClick={openDemo}
          data-testid="nav-demo-button"
          className="mp-cta-ghost"
        >
          Book Demo
          <ArrowRight className="h-3 w-3" />
        </button>
      </div>
    </div>

    <div className="relative z-10 max-w-[1200px] mx-auto text-center">
      <div className="space-y-8">
        <div className="mp-fade-up flex justify-center">
          <div
            className="inline-flex items-center gap-2 rounded-full border border-[#00FFFF]/25 bg-[#00FFFF]/[0.04] px-3.5 py-1.5"
            data-testid="hero-badge"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#00FFFF] opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#00FFFF] mp-pulse-dot" />
            </span>
            <span className="mp-eyebrow">24/7 Voice AI · Service Businesses</span>
          </div>
        </div>

        <h1
          data-testid="hero-headline"
          className="mp-headline mp-headline-uppercase mp-fade-up mp-delay-1 text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] text-[#F0F2F5]"
        >
          Never Miss Another
          <br />
          <span className="mp-cyan">Customer Call.</span>
        </h1>

        <p
          data-testid="hero-subheadline"
          className="mp-fade-up mp-delay-2 mx-auto max-w-2xl text-base md:text-lg lg:text-xl text-[#A0A0A0] font-light leading-relaxed"
        >
          Your AI receptionist answers every call 24/7, captures the lead, and
          books the appointment — even when you're out on a job.
        </p>

        <div className="mp-fade-up mp-delay-3 pt-4 flex flex-col sm:flex-row items-center justify-center gap-5">
          <PrimaryCta testId="hero-cta-button">Book a Free Demo</PrimaryCta>
          <div
            data-testid="hero-reassurance"
            className="flex items-center gap-2 text-xs sm:text-sm text-[#A0A0A0]"
          >
            <ShieldCheck className="h-4 w-4 text-[#00FFFF]/70" />
            <span className="uppercase tracking-wider">
              15-min walkthrough · No credit card
            </span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const MeetEmily = () => (
  <section
    data-testid="meet-emily-section"
    className="relative py-20 md:py-28 px-4 bg-[#0A0A0A] overflow-hidden"
  >
    <div
      aria-hidden="true"
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
      style={{ background: "#00FFFF", opacity: 0.05, filter: "blur(150px)" }}
    />
    <div className="relative max-w-[1100px] mx-auto">
      <div className="mp-glass mp-fade-up rounded-3xl p-8 md:p-12 lg:p-14 relative overflow-hidden">
        {/* top + bottom cyan accent lines */}
        <div
          aria-hidden="true"
          className="absolute top-0 left-0 right-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(0,255,255,0.5) 50%, transparent 100%)",
          }}
        />
        <div className="relative flex flex-col lg:flex-row items-center gap-10 lg:gap-14">
          {/* Phone visual */}
          <div className="flex-shrink-0 w-full lg:w-auto flex justify-center">
            <div className="relative">
              <div
                aria-hidden="true"
                className="absolute -inset-5 rounded-[2rem] opacity-30 blur-2xl"
                style={{ background: "#00FFFF" }}
              />
              <a
                href={`tel:${EMILY_PHONE_TEL}`}
                data-testid="emily-phone-visual"
                className="relative block bg-[#0A0A0A] border border-[#00FFFF]/30 rounded-[1.5rem] px-8 py-10 md:px-12 md:py-12 text-center group transition-all duration-300 hover:border-[#00FFFF]/70"
              >
                <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-[#00FFFF]/10 border border-[#00FFFF]/30 flex items-center justify-center transition-colors duration-300 group-hover:bg-[#00FFFF]/20">
                  <PhoneOutgoing
                    className="h-6 w-6 text-[#00FFFF]"
                    strokeWidth={2}
                  />
                </div>
                <div className="mp-eyebrow mb-2" style={{ fontSize: "0.65rem" }}>
                  Tap to Call
                </div>
                <div
                  className="mp-headline text-2xl md:text-3xl lg:text-4xl text-white tracking-tight"
                  style={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {EMILY_PHONE_DISPLAY}
                </div>
                <div
                  className="absolute left-1/2 -translate-x-1/2 -bottom-3 bg-[#00FFFF] text-[#0A0A0A] px-3.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] whitespace-nowrap"
                  style={{ fontFamily: "JetBrains Mono, monospace" }}
                >
                  Live Now
                </div>
              </a>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 text-center lg:text-left w-full">
            <div className="mp-eyebrow mb-4">Try It Yourself</div>
            <h2
              data-testid="meet-emily-title"
              className="mp-headline text-3xl sm:text-4xl md:text-5xl text-[#F0F2F5] leading-[1.05]"
            >
              Meet <span className="mp-cyan">Emily</span>
            </h2>
            <p className="mt-5 text-base md:text-lg text-[#A0A0A0] leading-relaxed max-w-xl mx-auto lg:mx-0">
              Our Voice AI is waiting to show you exactly how she can handle
              calls for <span className="text-white font-semibold">your</span>{" "}
              business. Give her a call and experience it firsthand.
            </p>

            <div className="mt-6 flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-5 justify-center lg:justify-start">
              {[
                "Live conversation",
                "No commitment",
                "See AI in action",
              ].map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-2 text-sm text-[#A0A0A0]"
                >
                  <CheckCircle2
                    className="h-4 w-4 text-[#00FFFF] flex-shrink-0"
                    strokeWidth={2}
                  />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-center gap-5 justify-center lg:justify-start">
              <button
                onClick={callEmily}
                data-testid="emily-call-button"
                className="mp-cta group"
              >
                <PhoneOutgoing className="h-4 w-4" strokeWidth={2.5} />
                <span>Call Emily Now</span>
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <div
                className="text-xs text-[#A0A0A0] uppercase tracking-[0.18em]"
                style={{ fontFamily: "JetBrains Mono, monospace" }}
              >
                Voice Demo · 24/7
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const HowItWorks = () => {
  const steps = [
    {
      n: "01",
      icon: PhoneCall,
      title: "Customer Calls Your Business",
      body: "AI answers instantly, no hold time — professional, human-sounding, always on.",
      testId: "step-1-card",
      delay: "mp-delay-1",
    },
    {
      n: "02",
      icon: UserRound,
      title: "AI Collects the Lead Info",
      body: "Captures their name, phone number, and the service they need in a natural conversation.",
      testId: "step-2-card",
      delay: "mp-delay-2",
    },
    {
      n: "03",
      icon: Mail,
      title: "You Get an Instant Notification",
      body: "Full lead details hit your inbox the moment the call ends — ready to close.",
      testId: "step-3-card",
      delay: "mp-delay-3",
    },
  ];

  return (
    <section
      data-testid="how-it-works-section"
      className="relative py-24 md:py-32 px-4 bg-[#0D0D0D] overflow-hidden"
    >
      <div className="mp-section-glow" aria-hidden="true" />
      <div className="relative max-w-[1400px] mx-auto">
        <div className="text-center mb-16">
          <Eyebrow>Process</Eyebrow>
          <h2
            data-testid="how-it-works-title"
            className="mp-headline mt-4 text-3xl sm:text-4xl md:text-5xl text-[#F0F2F5]"
          >
            How It <span className="mp-cyan">Works</span>
          </h2>
        </div>

        <ol className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-6 list-none p-0 m-0">
          {steps.map((s) => (
            <li
              key={s.n}
              data-testid={s.testId}
              className={`mp-fade-up ${s.delay} text-center md:text-left px-4 md:px-8 relative`}
            >
              <div className="mp-step-number mb-4">{s.n}</div>
              <div className="mp-icon-circle mb-5 mx-auto md:mx-0">
                <s.icon className="h-5 w-5 text-[#00FFFF]" strokeWidth={1.75} />
              </div>
              <h3 className="mp-headline text-xl md:text-2xl text-white mb-3">
                {s.title}
              </h3>
              <p className="text-sm md:text-base text-[#A0A0A0] leading-relaxed">
                {s.body}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

const WhoItsFor = () => {
  const industries = [
    { icon: Wrench, label: "HVAC & Plumbing", sub: "Emergency dispatch, 24/7 intake" },
    { icon: Droplets, label: "Appliance Repair", sub: "Capture every broken-appliance call" },
    { icon: Bug, label: "Pest Control", sub: "Book inspections while you're on-site" },
    { icon: Trees, label: "Landscaping", sub: "Schedule lawn & yard work effortlessly" },
    { icon: Car, label: "Auto Repair", sub: "Book service while your team works" },
    { icon: Phone, label: "Any Phone-Driven Business", sub: "If it rings, we can answer it" },
  ];

  return (
    <section
      data-testid="who-its-for-section"
      className="relative py-24 md:py-32 px-4 bg-[#0A0A0A] overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 md:mb-16">
          <div>
            <Eyebrow>Industries</Eyebrow>
            <h2
              data-testid="who-its-for-title"
              className="mp-headline mt-4 text-3xl sm:text-4xl md:text-5xl text-[#F0F2F5]"
            >
              Built for <br className="hidden md:block" />
              <span className="mp-cyan">Service Businesses</span>
            </h2>
          </div>
          <p className="hidden md:block mp-eyebrow text-[#A0A0A0]" style={{ color: "#A0A0A0" }}>
            6 core verticals
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {industries.map((item, idx) => (
            <div
              key={item.label}
              data-testid={`industry-card-${idx}`}
              className={`mp-glass mp-fade-up mp-delay-${Math.min(idx + 1, 4)} p-6 md:p-7 group`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-11 h-11 rounded-[6px] bg-[#00FFFF]/10 border border-[#00FFFF]/20 flex items-center justify-center transition-colors duration-300 group-hover:bg-[#00FFFF]/15">
                  <item.icon
                    className="h-[18px] w-[18px] text-[#00FFFF]"
                    strokeWidth={1.75}
                  />
                </div>
                <div className="min-w-0">
                  <h3 className="mp-headline text-base md:text-lg text-white leading-tight">
                    {item.label}
                  </h3>
                  <p className="mt-1.5 text-sm text-[#A0A0A0] leading-relaxed">
                    {item.sub}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const TestimonialCard = ({ quote, name, location, delay, testId }) => (
  <figure
    data-testid={testId}
    className={`mp-glass mp-fade-up ${delay} p-8 md:p-10 relative`}
  >
    <Quote
      className="absolute top-6 right-6 h-7 w-7 text-[#00FFFF]/25"
      strokeWidth={1.5}
    />
    <blockquote className="text-lg md:text-xl text-[#F0F2F5] leading-relaxed font-light">
      "{quote}"
    </blockquote>
    <figcaption className="mt-8 flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-[#00FFFF]/10 border border-[#00FFFF]/30 flex items-center justify-center">
        <span className="mp-cyan font-bold text-sm">{name.charAt(0)}</span>
      </div>
      <div className="flex flex-col">
        <span className="text-sm md:text-base font-semibold text-white">
          {name}
        </span>
        <span className="mp-eyebrow" style={{ color: "#A0A0A0", fontSize: "0.65rem" }}>
          {location}
        </span>
      </div>
    </figcaption>
  </figure>
);

const SocialProof = () => (
  <section
    data-testid="testimonials-section"
    className="relative py-24 md:py-32 px-4 bg-[#0D0D0D] overflow-hidden"
  >
    <div className="mp-section-glow" aria-hidden="true" />
    <div className="relative max-w-[1200px] mx-auto">
      <div className="text-center mb-16">
        <Eyebrow>Social Proof</Eyebrow>
        <h2
          data-testid="testimonials-title"
          className="mp-headline mt-4 text-3xl sm:text-4xl md:text-5xl text-[#F0F2F5]"
        >
          What Our <span className="mp-cyan">Clients Say</span>
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <TestimonialCard
          quote="We stopped missing after-hours calls completely. It paid for itself in the first week."
          name="Mike R."
          location="Houston, TX"
          delay="mp-delay-1"
          testId="testimonial-1"
        />
        <TestimonialCard
          quote="I was skeptical at first but the AI sounds completely natural. Customers don't even know."
          name="Sarah T."
          location="Dallas, TX"
          delay="mp-delay-2"
          testId="testimonial-2"
        />
      </div>
    </div>
  </section>
);

const FinalCTA = () => (
  <section
    data-testid="final-cta-section"
    className="relative py-28 md:py-40 px-4 bg-[#0A0A0A] overflow-hidden"
  >
    <div
      aria-hidden="true"
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full pointer-events-none"
      style={{
        background: "#00FFFF",
        opacity: 0.05,
        filter: "blur(150px)",
      }}
    />
    <div className="relative max-w-[1000px] mx-auto text-center">
      <div className="mp-fade-up">
        <Eyebrow>Get Started</Eyebrow>
      </div>
      <h2
        data-testid="final-cta-headline"
        className="mp-headline mp-headline-uppercase mp-fade-up mp-delay-1 mt-5 text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[#F0F2F5]"
      >
        Ready to Stop
        <br />
        <span className="mp-cyan">Missing Calls?</span>
      </h2>
      <p
        data-testid="final-cta-subheadline"
        className="mp-fade-up mp-delay-2 mt-7 mx-auto max-w-2xl text-base md:text-lg lg:text-xl text-[#A0A0A0] font-light leading-relaxed"
      >
        Book a free 15-minute demo and see exactly how it works for your business.
      </p>
      <div className="mp-fade-up mp-delay-3 mt-10 flex justify-center">
        <PrimaryCta testId="final-cta-button">Book Your Free Demo</PrimaryCta>
      </div>
    </div>
  </section>
);

const Footer = ({ onOpenPrivacy }) => (
  <footer
    data-testid="site-footer"
    className="relative bg-[#0A0A0A] border-t border-white/[0.06] py-12 px-4"
  >
    <div className="max-w-[1400px] mx-auto">
      <div className="mp-divider mb-10" />
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div className="flex flex-col gap-3">
          <Logo />
          <p
            className="text-xs text-[#A0A0A0]/70"
            style={{
              fontFamily: "JetBrains Mono, ui-monospace, monospace",
              letterSpacing: "0.12em",
            }}
          >
            © 2026 MODERN PATH AGENCY · ALL RIGHTS RESERVED
          </p>
        </div>
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
          <a
            href="mailto:contactus@modernpathagency.com"
            data-testid="footer-email-link"
            className="inline-flex items-center gap-2 text-sm text-[#A0A0A0] transition-colors duration-200 hover:text-[#00FFFF]"
          >
            <Mail className="h-4 w-4" />
            contactus@modernpathagency.com
          </a>
          <button
            onClick={onOpenPrivacy}
            data-testid="privacy-policy-link"
            className="text-left text-sm text-[#A0A0A0] transition-colors duration-200 hover:text-[#00FFFF] uppercase tracking-wider"
            style={{
              fontFamily: "JetBrains Mono, ui-monospace, monospace",
              fontSize: "0.72rem",
              letterSpacing: "0.18em",
            }}
          >
            Privacy Policy
          </button>
        </div>
      </div>
    </div>
  </footer>
);

const PrivacyModal = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      data-testid="privacy-policy-modal"
      className="bg-gradient-to-br from-[#0D0D0D] to-[#151515] border border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto rounded-2xl"
    >
      <DialogHeader>
        <div className="mp-eyebrow mb-2">Legal</div>
        <DialogTitle
          className="mp-headline text-2xl sm:text-3xl text-[#F0F2F5]"
        >
          Privacy <span className="mp-cyan">Policy</span>
        </DialogTitle>
        <DialogDescription className="text-[#A0A0A0]">
          How Modern Path Agency handles your information.
        </DialogDescription>
      </DialogHeader>
      <div className="mt-4 space-y-5 text-sm md:text-[15px] text-[#A0A0A0] leading-relaxed">
        <p>
          Modern Path Agency ("we", "us") respects your privacy. This page
          summarises what information we collect when you visit
          modernpathagency.com or book a demo through our scheduling link.
        </p>
        <div>
          <h4 className="text-white font-semibold mb-1.5 mp-headline text-base">
            Information we collect
          </h4>
          <p>
            We collect contact information you submit voluntarily (name, email,
            phone), and anonymised analytics such as page views, referrers, and
            device type via Google Analytics 4.
          </p>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-1.5 mp-headline text-base">
            How we use it
          </h4>
          <p>
            To respond to demo requests, deliver the services you request, and
            improve our website. We do not sell your personal information.
          </p>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-1.5 mp-headline text-base">
            Cookies
          </h4>
          <p>
            We use essential cookies for site functionality and analytics
            cookies to understand traffic patterns. You can disable non-essential
            cookies in your browser settings.
          </p>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-1.5 mp-headline text-base">
            Contact
          </h4>
          <p>
            Questions about this policy? Email{" "}
            <a
              href="mailto:contactus@modernpathagency.com"
              className="mp-cyan font-medium hover:underline"
            >
              contactus@modernpathagency.com
            </a>
            .
          </p>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export default function Landing() {
  const [privacyOpen, setPrivacyOpen] = useState(false);

  return (
    <main
      data-testid="landing-page"
      className="relative min-h-screen bg-[#0A0A0A] text-[#F0F2F5]"
    >
      <div className="noise-overlay" aria-hidden="true" />
      <Hero />
      <MeetEmily />
      <HowItWorks />
      <WhoItsFor />
      <SocialProof />
      <FinalCTA />
      <Footer onOpenPrivacy={() => setPrivacyOpen(true)} />
      <PrivacyModal open={privacyOpen} onOpenChange={setPrivacyOpen} />
    </main>
  );
}
